import { gql, useQuery } from '@apollo/client'
import * as React from 'react'
import styled from 'styled-components'
import Icon, { IconSize, propertyTypeToIconName } from '../../../../common/src/components/Icon'
import Indicator from '../../../../common/src/components/Indicator'
import { Link } from '../../../../common/src/components/Link'
import Tooltip from '../../../../common/src/components/Tooltip'
import { EllipsisSpan, Spacer, TextTag, BodyTextSpan } from '../../../../common/src/components/core'
import { ColorName } from '../../../../common/src/constants'
import { diffMins, getTimeAgo } from '../../../../common/src/date'
import Time from '../../../../common/src/time'
import {
  FetchInboxQuery,
  FetchTasksPageQuery,
  FetchTaskLinksQuery,
  FetchFirmTasksQuery,
  UpdateTasksMutation,
  LeasePropertyType,
  TaskType,
  FetchLatestPmSyncJobQuery,
  FetchLatestPmSyncJobQueryVariables,
} from '../../../../common/src/types.gen'
import { getTaskPath } from '../route'

interface TaskTextProps {
  task:
    | NonNullable<FetchInboxQuery['thread']>['leasingTask']
    | NonNullable<FetchTasksPageQuery['task']>
    | FetchTaskLinksQuery['contact']['tasks'][0]
    | FetchTasksPageQuery['allFirmTasks'][0]
    | FetchFirmTasksQuery['firm']['tasks'][0]
    | UpdateTasksMutation['bulkUpdateTasks'][0]
  align?: 'left' | 'right'
  iconSize?: IconSize
}

/**
 * Display a formatted task link. Formatting options let you choose:
 * - `align`: should an icon be shown to the left or right of the task's name?
 * - `iconSize`: what size should an icon be (small | medium | large)? No icon if not provided.
 */
export function TaskText(props: TaskTextProps) {
  const { task, align, iconSize } = props
  const { displayName, isArchived } = task
  const nameElem = <EllipsisSpan maxWidth="14em">{displayName}</EllipsisSpan>

  // if no icon is requested, just display name of the task
  if (!iconSize) {
    return nameElem
  }

  const showArchived = iconSize !== 'small' && isArchived

  // `right` returns `task name [icon] [ARCHIVED]`
  if (align === 'right') {
    return (
      <span>
        {nameElem}
        <Spacer width={iconSize === 'small' ? 8 : 12} />
        <TaskIcon task={task} size={iconSize} />
        {showArchived && <Spacer width={12} />}
        {showArchived && <TextTag color="midway">ARCHIVED</TextTag>}
      </span>
    )
  }

  // align undefined or `left` returns `[icon] task name [ARCHIVED]`
  return (
    <span>
      <TaskIcon task={task} size={iconSize} />
      <Spacer width={iconSize === 'small' ? 8 : 12} />
      {nameElem}
      {showArchived && <Spacer width={12} />}
      {showArchived && <TextTag color="midway">ARCHIVED</TextTag>}
    </span>
  )
}

interface TaskIconProps {
  task: { leasingInfo?: { propertyType?: LeasePropertyType | null } | null }
  size?: IconSize
  color?: ColorName
}

/**
 * Displays an `Icon` for the `LeasingTask` based on its propertyType (SENIOR | STUDENT | MF | SFR | etc.)
 */
export function TaskIcon(props: TaskIconProps) {
  const { task, size, color } = props
  const { leasingInfo } = task
  const propertyType = (leasingInfo && leasingInfo.propertyType) || TaskType.LEASING_FIRM
  const col = color || 'silver'
  return <Icon name={propertyTypeToIconName(propertyType)} color={col} size={size} />
}

type TaskLinkProps = TaskTextProps

/**
 * Clickable wrapper for `TaskText`. Navigate to the task page when clicked.
 */
export const TaskLink = (props: React.PropsWithChildren<TaskLinkProps>) => {
  const { task } = props
  return (
    <Link color="snow" to={getTaskPath(task.id)}>
      <TaskText {...props} />
    </Link>
  )
}

interface TaskTooltipLinkProps {
  task:
    | NonNullable<FetchInboxQuery['thread']>['leasingTask']
    | NonNullable<FetchTasksPageQuery['thread']>['leasingTask']
  align?: 'left' | 'right'
  iconSize?: IconSize
}

export const TaskTooltipLink = (props: React.PropsWithChildren<TaskTooltipLinkProps>) => {
  const { task } = props

  const firmId = task.firm.id

  let taskId
  if (task.taskType !== TaskType.LEASING_PROPERTY) {
    taskId = task.id
  }

  const { renderTaskLastSyncInfo } = useTaskLastSyncInfo(firmId, taskId)

  return (
    <Tooltip contentRenderer={renderTaskLastSyncInfo}>
      <TaskLink {...props} />
    </Tooltip>
  )
}

/**
 * Custom hook for `TaskTooltipLink`.
 */
function useTaskLastSyncInfo(firmId: number, taskId?: number) {
  const renderTaskLastSyncInfo = () => <TaskLastSyncInfo firmId={firmId} taskId={taskId} />
  return { renderTaskLastSyncInfo }
}

interface TaskLastSyncInfoProps {
  firmId: number
  taskId?: number
}

/**
 * Fetch the last time the task was synced with a PM system.
 *
 * Renders something like "AppFolio synced 11m ago".
 */
export const TaskLastSyncInfo = (props: React.PropsWithChildren<TaskLastSyncInfoProps>) => {
  const { firmId, taskId } = props
  const { loading, error, data } = useQuery<FetchLatestPmSyncJobQuery, FetchLatestPmSyncJobQueryVariables>(
    fetchLatestPmSyncJobQuery,
    { variables: { firmId, taskId }, fetchPolicy: 'cache-and-network' }
  )

  if (loading || error || !data || !data.latestPmSyncJob) {
    return null
  }

  const { pmTypeDisplayName, timeCreated } = data.latestPmSyncJob

  const mins = diffMins(Time.now(), data.latestPmSyncJob.timeCreated)

  return (
    <TaskLastSyncInfoRoot>
      <Indicator color={computeTaskIndicatorColor(mins)} />
      <Spacer width={8} />
      <BodyTextSpan>{`${pmTypeDisplayName} synced ${getTimeAgo(timeCreated, Time.now())}`}</BodyTextSpan>
    </TaskLastSyncInfoRoot>
  )
}

const TaskLastSyncInfoRoot = styled.div`
  padding: 4px 12px;
`

/**
 * @param mins number of minutes since last sync
 */
const computeTaskIndicatorColor = (mins: number): ColorName => {
  if (mins < 60) {
    return 'mint'
  } else if (mins < 120) {
    return 'lemon'
  } else {
    return 'coral'
  }
}

const fetchLatestPmSyncJobQuery = gql`
  query FetchLatestPmSyncJob($firmId: Int!, $taskId: Int) {
    latestPmSyncJob(firmId: $firmId, taskId: $taskId) {
      id
      timeCreated
      pmTypeDisplayName
    }
  }
`

interface FirmTaskTooltipLinkProps {
  // the leasing task that contains information about the firm task
  task:
    | NonNullable<FetchInboxQuery['thread']>['leasingTask']
    | NonNullable<FetchTasksPageQuery['thread']>['leasingTask']
}

export const FirmTaskTooltipLink = (props: FirmTaskTooltipLinkProps) => {
  const { task } = props

  const firmTaskId = task.firmTaskId
  const firmName = task.firm.firmName

  if (firmTaskId == null || task.taskType === 'LEASING_FIRM') {
    return null
  }

  const { renderTaskLastSyncInfo } = useTaskLastSyncInfo(task.firm.id)

  return (
    <Tooltip contentRenderer={renderTaskLastSyncInfo}>
      <Link color="snow" to={getTaskPath(firmTaskId)}>
        <Icon color="silver" name="task-firm" />
        <Spacer width={8} />
        {firmName}
      </Link>
    </Tooltip>
  )
}
